"use client"

import { Suspense, useRef, useState } from "react"
import { useSearchParams } from "next/navigation"
import { motion } from "motion/react"

import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { HeadlineHero, LawnPrefillBanner, RelatedTools, ResultEmailCaptureCard, ResultSuccessCard, ToolPageLoadingFallback } from "@/components/tools/shared"
import type { ResultSuccessCardStat } from "@/components/tools/shared"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { Sprout, Leaf, Ruler } from "lucide-react"
import { SeedingForm } from "@/components/tools/seeding-form"
import { SeedingPrepChecklist } from "@/components/tools/seeding-prep-checklist"
import { SeedingMistakes } from "@/components/tools/seeding-mistakes"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { CuratedProductPicks } from "@/components/tools/shared/curated-product-picks"
import { getSeedingPicks } from "@/lib/seeding-calculator/product-picks"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"

import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { useDefaultLawn } from "@/hooks/use-default-lawn"
import {
  useToolFromQuery,
  parseGrassType,
  parseZipCode,
  type QueryParser,
} from "@/hooks/useToolFromQuery"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"
import { useFromTool } from "@/hooks/use-from-tool"
import { analytics, toolNavigationAnalytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import type { SeedingResult } from "@/lib/seeding-calculator"

// Seeding accepts an extra "other" alias mapped to the calculator's
// "Other/Not Listed" key, so wrap the shared parser.
const parseSeedingGrass: QueryParser<string> = (raw) => {
  const matched = parseGrassType(raw)
  if (matched) return matched
  if (raw.toLowerCase().includes("other")) return "Other/Not Listed"
  return undefined
}

function SeedingCalculatorContent() {
  const searchParams = useSearchParams()
  useFromTool("seeding-calculator")
  const {
    trackUsage,
    showNewsletterModal,
    closeNewsletterModal,
    usageCount,
  } = useNewsletterPrompt()
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill, settled: lawnsSettled } = useDefaultLawn()
  const [initialZip, setInitialZip] = useState<string | undefined>(undefined)
  const [initialGrass, setInitialGrass] = useState<string | undefined>(undefined)
  const [liveResult, setLiveResult] = useState<SeedingResult | null>(null)
  // Verdict-driven "Remind me" modal — separate from the passive newsletter
  // prompt so user-initiated opens always work, even after dismissal.
  const [remindModalOpen, setRemindModalOpen] = useState(false)
  const trackedUsageRef = useRef(false)

  useToolFromQuery({
    parsers: { zip: parseZipCode, grass: parseSeedingGrass },
    required: ["zip", "grass"] as const,
    onAutoSubmit: ({ zip, grass }) => {
      setInitialZip(zip)
      setInitialGrass(grass)
    },
  })

  const handleResultChange = (result: SeedingResult | null) => {
    setLiveResult(result)
    if (result && !trackedUsageRef.current) {
      trackedUsageRef.current = true
      trackUsage("seeding-calculator")
      analytics.calculatorUsed("seeding", {
        inputs: {
          grass_type: result.grassType,
          grass_season: result.grassSeason,
          zip_code: result.zipCode,
          region: result.region,
        },
        result: {
          can_seed_now: result.canSeedNow,
          status: result.status,
          status_color: result.statusColor,
          lawn_size_sqft: result.seedQuantityEstimate?.lawnSizeSqft,
        },
        source: "live_update",
      })
    }
  }

  // Result-matched seed + starter for the user's grass. Empty for grasses with
  // no catalog seed (e.g. St. Augustine), where the generic block renders instead.
  const seedingPicks = liveResult ? getSeedingPicks(liveResult) : []

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Seeding Calculator" },
          ]}
        />

        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="mb-8 md:mb-12">
            <HeadlineHero
              eyebrow="Soil-temperature aware"
              title="Is it too late to"
              accent="seed?"
              subtitle="Free lawn seeding calculator by ZIP and grass type. Two inputs and the verdict updates live — no submit. Get your optimal seeding window, seed quantity per 1,000 sq ft, and whether it's too late to seed this year."
            />
          </div>

          {/* Live form + embedded preview is the whole above-the-fold surface */}
          <div className="mb-8">
            {/* Hold the banner's slot until the lawn fetch settles (CLS audit
                2026-07-30): anon users settle in the mount effect (placeholder
                never paints), but for signed-in users the banner used to mount
                after the Supabase fetch and push the whole form down. */}
            {!lawnsSettled ? (
              <div className="mb-4 min-h-[64px] sm:min-h-[46px] rounded-lg bg-green-50/40" aria-hidden />
            ) : (
              <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />
            )}
            <SeedingForm
              initialZip={initialZip ?? lawnPrefill.zipCode}
              initialGrass={initialGrass ?? lawnPrefill.grassType}
              initialLawnSize={lawnPrefill.lawnSizeSqft}
              initialSunlight={searchParams.get("sunlight") ?? lawnPrefill.sunlight}
              onResultChange={handleResultChange}
              onRequestReminder={() => setRemindModalOpen(true)}
            />
          </div>

          {liveResult && (
            <div className="mt-8 space-y-6">
              <SeedingSuccessCard
                result={liveResult}
                initialZip={initialZip}
                lawnPrefillZip={lawnPrefill.zipCode}
                initialSunlight={searchParams.get("sunlight") ?? lawnPrefill.sunlight}
              />
              <ResultEmailCaptureCard
                source="tool_result_seeding"
                toolDisplayName="seeding plan"
                subcopy="Seasonal reminders to watch the conditions before you sow."
                grassType={liveResult.grassType}
                zipCode={initialZip ?? lawnPrefill.zipCode}
                accountLead={{
                  redirectTo: buildToolUrl("seeding-calculator", {
                    grassType: liveResult.grassType,
                    zip: initialZip ?? lawnPrefill.zipCode,
                  }),
                  headline: `Save my seeding plan for ${liveResult.grassType}`,
                  subcopy: "Keep this seeding plan in a free account and track your lawn over time.",
                  dividerLabel: "or get your 12-month calendar by email",
                  onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_seeding"),
                }}
                onCaptured={() => {
                  closeNewsletterModal()
                }}
              />
            </div>
          )}

          {/* Below the fold: prep checklist + mistakes */}
          <SeedingPrepChecklist />
          <SeedingMistakes />

          <RelatedTools
            currentToolId="seeding-calculator"
            prefillValues={{
              zip: initialZip ?? lawnPrefill.zipCode,
              grassType: liveResult?.grassType ?? initialGrass ?? lawnPrefill.grassType,
              lawnSize: lawnPrefill.lawnSizeSqft,
              sunlight: searchParams.get("sunlight") ?? lawnPrefill.sunlight,
            }}
          />

          <ProductGuideCta source="seeding_calculator_result" variant="result" />

          {/* Result-matched seed + starter once we have a verdict; the generic
              category block otherwise (and for grasses with no catalog seed). */}
          <div className="mt-8">
            {seedingPicks.length > 0 ? (
              <CuratedProductPicks
                picks={seedingPicks}
                title="Seed and starter for your lawn"
                description={liveResult ? `Matched to ${liveResult.grassType}` : undefined}
                trackingSlug="tool:seeding-calculator"
              />
            ) : (
              <ProductRecommendations
                categories={["Grass Seed & Lawn Repair", "Fertilizers & Soil Health"]}
                title="Seeding Supplies"
                description="Top-rated grass seed and starter fertilizers for new lawns and overseeding"
                trackingContext="seeding-calculator"
                compact
              />
            )}
          </div>
        </div>
      </Reveal>

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Seeding Calculator"
        usageCount={usageCount}
        grassType={liveResult?.grassType}
        zipCode={initialZip ?? lawnPrefill.zipCode}
      />
      {/* Verdict-driven reminder modal — fires on the "Remind me" CTA when the
          current verdict is too-late/wait. Always usable, independent of the
          passive newsletter eligibility logic. */}
      <NewsletterSignupModal
        isOpen={remindModalOpen}
        onClose={() => setRemindModalOpen(false)}
        toolName="Seeding Calculator"
        usageCount={usageCount}
        grassType={liveResult?.grassType}
        zipCode={initialZip ?? lawnPrefill.zipCode}
      />
    </div>
  )
}

/**
 * Bounded peak-moment frame for the seeding result. When a seed quantity
 * estimate is available the hero shows the overseeding lb range; otherwise
 * the card falls back to interpretation + CTA only. Next-action: watering
 * schedule (new seed needs precise watering to germinate).
 */
function SeedingSuccessCard({
  result,
  initialZip,
  lawnPrefillZip,
  initialSunlight,
}: {
  result: SeedingResult
  initialZip?: string
  lawnPrefillZip?: string | null
  initialSunlight?: string | null
}) {
  const zip = initialZip ?? lawnPrefillZip
  const seedQty = result.seedQuantityEstimate

  const wateringHref = buildRelatedToolUrl("watering-schedule", "seeding-calculator", {
    zip,
    grassType: result.grassType,
    lawnSize: seedQty?.lawnSizeSqft,
    sunlight: initialSunlight,
  })

  const share = {
    // zip + grass round-trip (useToolFromQuery parsers) and sunlight round-trips
    // (page reads ?sunlight into initialSunlight). lawnSize is dropped: nothing
    // reads ?size into the seeding form, so it wouldn't reconstruct the size.
    url: buildToolUrl("seeding-calculator", {
      zip,
      grassType: result.grassType,
      sunlight: initialSunlight,
    }),
    title: "Seeding Window",
    text: `Seeding window for my ${result.grassType} lawn`,
    trackingId: "seeding-calculator",
  }

  const handleCtaClick = () => {
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: "seeding-calculator",
      targetTool: "watering-schedule",
      rank: 1,
      placement: "success_card",
      recommendationReason: "New seed needs precise watering to germinate",
      prefillFields: ["zip", "grass", "size"]
        .filter((f) => {
          if (f === "zip") return Boolean(zip)
          if (f === "size") return Boolean(seedQty?.lawnSizeSqft)
          return true
        })
        .join(","),
    })
  }

  const canSeed = result.canSeedNow

  const fmtDate = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" })

  if (!canSeed) {
    return (
      <ResultSuccessCard
        eyebrow={result.status === "too_late" ? "Too late this season" : "Wait for better timing"}
        interpretation={result.statusMessage}
        cta={{
          href: wateringHref,
          label: "Plan germination watering",
          onClick: handleCtaClick,
        }}
        share={share}
      />
    )
  }

  const windowRange = result.primaryWindow
    ? ` — ideal window is ${fmtDate(result.primaryWindow.optimalStart)} to ${fmtDate(result.primaryWindow.optimalEnd)}`
    : ""

  // Decision-useful secondary metrics already on seedQuantityEstimate: new-lawn
  // seed lbs (full renovation rate), overseeding lbs (the hero restated), and
  // coverage area (confirms the calc ran against the right size). All guarded
  // behind seedQty — the prop is optional on the result object.
  const stats: ResultSuccessCardStat[] = []
  if (seedQty) {
    stats.push(
      { icon: Sprout, label: "Overseed rate", value: `${seedQty.overseedPounds} lbs` },
      { icon: Leaf, label: "New lawn rate", value: `${seedQty.newLawnPounds} lbs` },
      { icon: Ruler, label: "Coverage", value: `${seedQty.lawnSizeSqft.toLocaleString()} sqft` },
    )
  }

  return (
    <ResultSuccessCard
      eyebrow="Good time to seed"
      heroValue={seedQty ? seedQty.overseedPounds : undefined}
      heroLabel={seedQty ? `to overseed your ${seedQty.lawnSizeSqft.toLocaleString()} sq ft lawn` : undefined}
      stats={stats.length > 0 ? stats : undefined}
      interpretation={
        <>
          {result.grassType} is a {result.grassSeason.toLowerCase()}-season grass{windowRange}. {result.statusMessage}
        </>
      }
      cta={{
        href: wateringHref,
        label: "Plan germination watering",
        onClick: handleCtaClick,
      }}
      share={share}
    />
  )
}

export function SeedingCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="seeding-calculator" />}>
      <SeedingCalculatorContent />
    </Suspense>
  )
}
